/**
 * One Replace browser for every section (Builder Coherence BC-5).
 *
 * Replace used to have two homes. The section list's row thumbnail opened the
 * variant picker (ui/variant-picker/session.js) for page AND shared sections;
 * row 2's Replace and the section menu emitted `section:request-swap`, which
 * the property panel answered with its own swap grid — hand-rolled cards that
 * searched the ACTIVE PAGE only. So Replace on the shared navigation or footer
 * was an enabled button that did nothing, with nothing to say about it (audit
 * item 5), and the two surfaces offered the same variants in two different
 * chromes (items 20, 21).
 *
 * `section:request-swap` lands here now and opens the variant picker beside
 * the control that asked, for either kind of section. The panel's grid stays
 * reachable behind `localStorage['pb-replace-legacy'] = '1'` until the manual
 * checklist retires it; its interpolations are escaped meanwhile.
 *
 * The picker is a dynamic import, as it is from the section list: a surface
 * most sessions never open must not grow the boot graph.
 */

import { reportError } from '../core/report-error.js';
import { resolveSectionByInstance } from '../editing/section-utils.js';
import { getSharedType } from '../sections/section-manager.js';
import { getCategoryMap } from '../shared/section-display-name.js';
import { showToast } from './toast.js';

export const REPLACE_LEGACY_KEY = 'pb-replace-legacy';

/** The kill switch: `'1'` keeps the property panel's own swap grid. */
export function replaceLegacyEnabled() {
  try {
    return localStorage.getItem(REPLACE_LEGACY_KEY) === '1';
  } catch {
    // Expected flow: storage denied (private browsing) — the new browser.
    return false;
  }
}

/**
 * The section list row for an instance, when the list is on screen — the
 * picker's natural anchor when the control that asked has no box of its own
 * (a command palette entry, a keyboard shortcut).
 */
function sectionListRow(instanceId) {
  try {
    return document.querySelector(`.pb-sl-row[data-instance-id="${instanceId}"]`) || null;
  } catch {
    return null;
  }
}

/**
 * Open the variant picker for `instanceId` — a page section or the shared
 * navigation/footer. Resolves when the picker closes (or at once when it did
 * not open). Never rejects, and never silent: a Replace that does nothing is
 * the audit's finding, so a target that left, a category with nothing else to
 * offer, and a failure to open each say so.
 *
 * @param {{instanceId: string, triggerEl?: HTMLElement|null}} req
 * @returns {Promise<{opened: boolean, reason?: string}>}
 */
export async function openReplaceBrowser({ instanceId, triggerEl = null } = {}) {
  if (!instanceId) return { opened: false, reason: 'no-target' };
  const resolved = resolveSectionByInstance(instanceId);
  if (!resolved?.section) {
    reportError('replace-browser', new Error(`no section for ${instanceId}`), {
      user: 'toast',
      message: 'That section is no longer here.'
    });
    return { opened: false, reason: 'gone' };
  }
  try {
    const [{ showVariantPicker }, categoryMap] = await Promise.all([
      import('./variant-picker/session.js'),
      getCategoryMap()
    ]);
    const outcome = await showVariantPicker({
      section: resolved.section,
      sharedType: getSharedType(instanceId),
      categoryMap,
      anchor: { triggerEl, rowEl: triggerEl ? null : sectionListRow(instanceId) }
    });
    if (outcome?.reason === 'no-variants') {
      showToast('This section has no other variants to choose from.', 'info');
    }
    return outcome || { opened: false, reason: 'unknown' };
  } catch (err) {
    reportError('replace-browser', err, {
      user: 'toast',
      message: 'Could not open the variant browser. Try again.'
    });
    return { opened: false, reason: 'error' };
  }
}
